<template>
  <Modal v-model="isOpen" :show-close="true">
    <template #title>{{ t('title') }}</template>

    <form class="flex flex-col gap-4" @submit.prevent="onSubmit">
      <Datepicker
        :model-value="quoteStartDate"
        :label="t('quoteStart')"
        :min-date="minQuoteStartDate"
        :max-date="maxQuoteStartDate"
        :clearable="false"
        class="w-full"
        @update:model-value="handleQuoteStartChange"
      />
      <Input
        v-model="customTotalBasePrice"
        type="number"
        :max-fraction-digits="2"
        :min="0"
        adornment="€"
        :label="t('thirtyDayPrice')"
      />
      <Checkbox
        v-if="hasPickupTransfer"
        v-model="invoicePickupTransfer"
        :label="t('invoicePickupTransfer')"
      />
      <Checkbox
        v-if="hasDropoffTransfer"
        v-model="invoiceDropoffTransfer"
        :label="t('invoiceDropoffTransfer')"
      />
      <ExtrasForm
        v-if="conversionPricing"
        :price-calculation="conversionPricing"
        :show-title="true"
        @update:booked-extras="pendingBookedExtras = $event"
        @update:custom-booked-extras="pendingCatalogCustomExtras = $event"
      />
      <CustomExtrasFields :booking="booking" :exclude-extra-types="hiddenCustomExtraTypes" />
      <div class="flex justify-end gap-2">
        <CVButton variant="secondary" :disabled="isSubmitting" @click.prevent="isOpen = false">
          {{ t('cancel') }}
        </CVButton>
        <CVButton variant="default" type="submit" :is-loading="isSubmitting">
          {{ t('convert') }}
        </CVButton>
      </div>
    </form>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import Modal from '@/components/Modal.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Input from '@/components/Input.vue';
import Datepicker from '@/components/Datepicker.vue';
import ExtrasForm, { type BookedExtraSelection } from '@/components/extras/ExtrasForm.vue';
import CustomExtrasFields from '@/components/booking-forms/CustomExtrasFields.vue';
import Checkbox from '@/components/Checkbox.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import {
  getBookedExtras,
  getCustomBookedExtras,
  asShortTermPricing,
  DEFAULT_BOOKING_TIMEZONE,
} from '@/utils/pricing.utils';
import { periodExtraOptionPrice } from '@/utils/period-extra-option-price';
import { REGULAR_TAX_PERCENT } from '@/utils/tax-procedure';
import { calculateRentalDays } from '@/utils/calculate-rental-days';
import { roundDecimals } from '@/utils/round-decimals';
import { toLocalDateForPicker } from '@/utils/calendar-date';
import { customExtrasSchema } from '@/validation/price-calculation.schema';
import { VehicleExtraType, type CustomBookedExtraDto } from '@carvia/ros-client-types';

const props = defineProps<{
  booking: Booking;
  isSubmitting?: boolean;
}>();

const isOpen = defineModel<boolean>({ required: true });

const emit = defineEmits<{
  (
    e: 'convert',
    payload: {
      quoteStartDate: string;
      customTotalBasePrice: number;
      bookedExtras: BookedExtraSelection[];
      customBookedExtras: CustomBookedExtraDto[];
      invoicePickupTransfer?: boolean;
      invoiceDropoffTransfer?: boolean;
    },
  ): void;
}>();

const { t } = useI18n();

const hiddenCustomExtraTypes = [VehicleExtraType.TRANSFER_PICKUP, VehicleExtraType.TRANSFER_RETURN];

const berlinIsoDay = (date: Date) =>
  DateTime.fromJSDate(date).setZone(DEFAULT_BOOKING_TIMEZONE).toISODate() ?? '';

const minQuoteStartIso = computed(() => {
  const pickup = berlinIsoDay(new Date(props.booking.startDate));
  const tomorrow = DateTime.now().setZone(DEFAULT_BOOKING_TIMEZONE).plus({ days: 1 }).toISODate();
  return pickup && tomorrow && pickup > tomorrow ? pickup : (tomorrow ?? '');
});

const maxQuoteStartIso = computed(() => {
  const dayBeforeEnd = DateTime.fromJSDate(new Date(props.booking.endDate))
    .setZone(DEFAULT_BOOKING_TIMEZONE)
    .minus({ days: 1 })
    .toISODate();
  return dayBeforeEnd ?? minQuoteStartIso.value;
});

const minQuoteStartDate = computed(() => toLocalDateForPicker(minQuoteStartIso.value));
const maxQuoteStartDate = computed(() => toLocalDateForPicker(maxQuoteStartIso.value));

const suggestedThirtyDayPrice = () => {
  const pricing = asShortTermPricing(props.booking.priceCalculation);
  if (!pricing) return 0;
  const days = calculateRentalDays(
    new Date(props.booking.startDate),
    new Date(props.booking.endDate),
  );
  const base = pricing.customTotalBasePrice ?? pricing.totalBasePrice ?? 0;
  return days > 0 ? roundDecimals((base / days) * 30, 2) : 0;
};

const conversionPricing = computed(() => {
  const pricing = asShortTermPricing(props.booking.priceCalculation);
  if (!pricing) return props.booking.priceCalculation;
  const taxPercent = pricing.taxPercent ?? REGULAR_TAX_PERCENT;
  return {
    ...pricing,
    days: 30,
    availableExtras: (pricing.availableExtras ?? []).map((extra) => ({
      ...extra,
      options: (extra.options ?? []).map((option) => ({
        ...option,
        calculatedPrice: periodExtraOptionPrice(option, 30, taxPercent, true),
      })),
    })),
  };
});

const editableCustomExtras = (extras: CustomBookedExtraDto[] | null | undefined) =>
  (extras ?? []).filter(
    (extra) =>
      !extra.isAdditional &&
      extra.customVehicleExtraType !== VehicleExtraType.TRANSFER_PICKUP &&
      extra.customVehicleExtraType !== VehicleExtraType.TRANSFER_RETURN,
  );

const additionalAndTransfers = (extras: CustomBookedExtraDto[] | null | undefined) =>
  (extras ?? []).filter(
    (extra) =>
      extra.isAdditional ||
      extra.customVehicleExtraType === VehicleExtraType.TRANSFER_PICKUP ||
      extra.customVehicleExtraType === VehicleExtraType.TRANSFER_RETURN,
  );

const currentCustomExtras = () =>
  props.booking.priceCalculation ? getCustomBookedExtras(props.booking.priceCalculation) : [];

const { handleSubmit, resetForm } = useForm({
  initialValues: {
    customBookedExtras: editableCustomExtras(currentCustomExtras()),
  },
  validationSchema: customExtrasSchema,
});

const quoteStartDate = ref<Date>(minQuoteStartDate.value);
const customTotalBasePrice = ref(suggestedThirtyDayPrice());
const pendingBookedExtras = ref<BookedExtraSelection[]>([]);
const pendingCatalogCustomExtras = ref<CustomBookedExtraDto[] | undefined>(undefined);
const invoicePickupTransfer = ref(false);
const invoiceDropoffTransfer = ref(false);

const hasPickupTransfer = computed(() =>
  currentCustomExtras().some(
    (extra) => extra.customVehicleExtraType === VehicleExtraType.TRANSFER_PICKUP,
  ),
);

const hasDropoffTransfer = computed(() =>
  currentCustomExtras().some(
    (extra) => extra.customVehicleExtraType === VehicleExtraType.TRANSFER_RETURN,
  ),
);

const defaultInvoiceTransfer = () =>
  berlinIsoDay(quoteStartDate.value) === berlinIsoDay(new Date(props.booking.startDate));

const handleQuoteStartChange = (value: Date | Date[] | string | null) => {
  const next = Array.isArray(value) ? value[0] : value;
  if (!next) return;
  quoteStartDate.value = next instanceof Date ? next : new Date(next);
};

watch(isOpen, (open) => {
  if (!open) return;
  quoteStartDate.value = minQuoteStartDate.value;
  customTotalBasePrice.value = suggestedThirtyDayPrice();
  pendingBookedExtras.value = [];
  pendingCatalogCustomExtras.value = undefined;
  invoicePickupTransfer.value = defaultInvoiceTransfer();
  invoiceDropoffTransfer.value = defaultInvoiceTransfer();
  resetForm({
    values: {
      customBookedExtras: editableCustomExtras(currentCustomExtras()),
    },
  });
});

watch(quoteStartDate, () => {
  invoicePickupTransfer.value = defaultInvoiceTransfer();
  invoiceDropoffTransfer.value = defaultInvoiceTransfer();
});

const onSubmit = handleSubmit((formValues) => {
  const pricing = props.booking.priceCalculation;
  if (!pricing) return;
  const bookedExtras = (
    pendingBookedExtras.value.length
      ? pendingBookedExtras.value
      : getBookedExtras(pricing).map((extra) => ({
          vehicleExtraId: extra.vehicleExtraSnapshot?.id,
          vehicleExtraOptionId: extra.vehicleExtraOptionSnapshot?.id,
        }))
  ).filter(
    (extra) =>
      extra.vehicleExtraId &&
      extra.vehicleExtraOptionId &&
      !['custom', 'unchanged', 'individual'].includes(extra.vehicleExtraOptionId),
  );
  const catalogCustom = (pendingCatalogCustomExtras.value ?? []).filter(
    (extra) => extra.customVehicleExtraType === VehicleExtraType.EXTRA_KM,
  );
  const day = DateTime.fromJSDate(quoteStartDate.value).toISODate() ?? minQuoteStartIso.value;
  emit('convert', {
    quoteStartDate: DateTime.fromISO(day, { zone: DEFAULT_BOOKING_TIMEZONE }).toISO() ?? day,
    customTotalBasePrice: Number(customTotalBasePrice.value),
    bookedExtras,
    customBookedExtras: [
      ...additionalAndTransfers(currentCustomExtras()),
      ...catalogCustom,
      ...(formValues.customBookedExtras ?? []),
    ],
    ...(hasPickupTransfer.value ? { invoicePickupTransfer: invoicePickupTransfer.value } : {}),
    ...(hasDropoffTransfer.value ? { invoiceDropoffTransfer: invoiceDropoffTransfer.value } : {}),
  });
});
</script>

<i18n lang="json">
{
  "en": {
    "title": "Convert to long-term",
    "quoteStart": "First pricing period start",
    "thirtyDayPrice": "30-day vehicle price",
    "invoicePickupTransfer": "Invoice delivery transfer",
    "invoiceDropoffTransfer": "Invoice return transfer",
    "cancel": "Cancel",
    "convert": "Convert"
  },
  "de": {
    "title": "In Langzeit umwandeln",
    "quoteStart": "Beginn des ersten Preiszeitraums",
    "thirtyDayPrice": "Fahrzeugpreis für 30 Tage",
    "invoicePickupTransfer": "Anlieferung in Rechnung stellen",
    "invoiceDropoffTransfer": "Rückholung in Rechnung stellen",
    "cancel": "Abbrechen",
    "convert": "Umwandeln"
  },
  "es": {
    "title": "Convertir a larga duración",
    "quoteStart": "Inicio del primer período de precio",
    "thirtyDayPrice": "Precio del vehículo a 30 días",
    "invoicePickupTransfer": "Facturar el traslado de entrega",
    "invoiceDropoffTransfer": "Facturar el traslado de devolución",
    "cancel": "Cancelar",
    "convert": "Convertir"
  }
}
</i18n>
